import { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../api/client';
import { useAuth } from '../../hooks/useAuth';
import { usePermission } from '../../hooks/usePermission';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Select from '../../components/common/Select';
import Modal from '../../components/common/Modal';
import Badge from '../../components/common/Badge';
import Pagination from '../../components/common/Pagination';
import ChatBox from '../../components/chat/ChatBox';
import NotesTab from '../../components/talkrooms/NotesTab';
import ScheduleTab from '../../components/talkrooms/ScheduleTab';
import FoldersTab from '../../components/talkrooms/FoldersTab';
import ContentsTab from '../../components/talkrooms/ContentsTab';

type RightPanelTabType = 'notes' | 'schedule' | 'folders' | 'contents';

interface User {
  id: string;
  name: string;
  email: string;
}

interface RoomMember {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  role: string;
  joinedAt: string;
}

interface TalkRoom {
  id: string;
  name: string;
  description: string;
  roomType: 'public' | 'private' | 'direct';
  memberCount: number;
  updatedAt: string;
  lastMessage?: string;
}

// 相対時間を表示するヘルパー関数
const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return '今';
  if (diffMins < 60) return `${diffMins}分前`;
  if (diffHours < 24) return `${diffHours}時間前`;
  if (diffDays < 7) return `${diffDays}日前`;
  
  return date.toLocaleDateString('ja-JP', { month: 'short', day: 'numeric' });
};

const ALLOWED_PAGE_SIZES = [10, 20, 50] as const;

const parsePositiveInt = (value: string | null, fallback: number, min: number, max: number): number => {
  const num = Number(value);
  if (!Number.isFinite(num)) return fallback;
  const normalized = Math.trunc(num);
  if (normalized < min || normalized > max) return fallback;
  return normalized;
};

export default function TalkRoomList() {
  const { user: currentUser } = useAuth();
  const { canCreate, isAdmin } = usePermission();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rooms, setRooms] = useState<TalkRoom[]>([]);
  const [selectedRoom, setSelectedRoom] = useState<TalkRoom | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [showRoomList, setShowRoomList] = useState(true);
  
  // 右パネル
  const [showRightPanel, setShowRightPanel] = useState(true);
  const [activeTab, setActiveTab] = useState<RightPanelTabType>('notes');
  
  // ルーム作成
  const [newRoomName, setNewRoomName] = useState('');
  const [newRoomDescription, setNewRoomDescription] = useState('');
  const [newRoomType, setNewRoomType] = useState<'public' | 'private'>('public');
  const [creating, setCreating] = useState(false);
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([]);

  // メンバー管理
  const [members, setMembers] = useState<RoomMember[]>([]);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [loadingMembers, setLoadingMembers] = useState(false);

  // Toast / Confirm 状態
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [removeMemberTarget, setRemoveMemberTarget] = useState<RoomMember | null>(null);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(timer);
  }, [toast]);
  
  // ページネーション・ソート・表示件数（URLから取得）
  const currentPage = parsePositiveInt(searchParams.get('page'), 1, 1, 100000);
  const rawPageSize = parsePositiveInt(searchParams.get('pageSize'), 20, 1, 1000);
  const pageSize = ALLOWED_PAGE_SIZES.includes(rawPageSize as (typeof ALLOWED_PAGE_SIZES)[number]) ? rawPageSize : 20;
  const setCurrentPage = (page: number) => {
    setSearchParams(prev => { prev.set('page', String(page)); return prev; });
  };
  const setPageSize = (size: number) => {
    setSearchParams(prev => { prev.set('pageSize', String(size)); prev.delete('page'); return prev; });
  };
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({
    key: searchParams.get('sortBy') || 'updatedAt',
    direction: (searchParams.get('sortOrder') as 'asc' | 'desc') || 'desc',
  });
  const searchKeyword = searchParams.get('q') || '';
  const setSearchKeyword = (q: string) => {
    setSearchParams(prev => {
      if (q) { prev.set('q', q); } else { prev.delete('q'); }
      prev.delete('page');
      return prev;
    });
  };
  
  // Refs
  const roomListRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchRooms();
    fetchAvailableUsers();

    const pollInterval = setInterval(() => {
      fetchRooms(true);
    }, 30000);
    return () => clearInterval(pollInterval);
  }, []);

  useEffect(() => {
    if (selectedRoom) {
      if (window.innerWidth < 768) {
        setShowRoomList(false);
      }
    }
  }, [selectedRoom]);

  // 画面サイズに応じた右パネルの初期表示
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setShowRightPanel(false);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchRooms = async (isPolling = false) => {
    if (!isPolling) setLoading(true);
    try {
      const data = await api.get<{ items: TalkRoom[] }>('/api/talkrooms');
      const items = Array.isArray(data?.items) ? data.items : [];
      setRooms(items);
      if (selectedRoom) {
        const updated = items.find(r => r.id === selectedRoom.id);
        if (updated) setSelectedRoom(updated);
      }
      if (items.length > 0 && !selectedRoom) {
        const urlRoomId = searchParams.get('roomId');
        const urlRoom = urlRoomId ? items.find(r => r.id === urlRoomId) : null;
        if (urlRoom) {
          setSelectedRoom(urlRoom);
        } else if (window.innerWidth >= 1024) {
          setSelectedRoom(items[0]);
          setSearchParams(prev => { prev.set('roomId', items[0].id); return prev; }, { replace: true });
        }
      }
    } catch (error) {
      console.error('Failed to fetch talk rooms:', error);
      if (!isPolling) setRooms([]);
    } finally {
      if (!isPolling) setLoading(false);
    }
  };

  // フィルタ・ソート・ページネーション済みルーム
  const filteredAndSortedRooms = useMemo(() => {
    let filtered = rooms;
    
    // キーワード検索
    if (searchKeyword.trim()) {
      const keyword = searchKeyword.toLowerCase();
      filtered = filtered.filter(room => 
        room.name.toLowerCase().includes(keyword) ||
        (room.description && room.description.toLowerCase().includes(keyword))
      );
    }
    
    // ソート
    return [...filtered].sort((a, b) => {
      const aValue = a[sortConfig.key as keyof TalkRoom];
      const bValue = b[sortConfig.key as keyof TalkRoom];
      if (aValue === undefined || aValue === null) return 1;
      if (bValue === undefined || bValue === null) return -1;
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }
      return sortConfig.direction === 'asc'
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
  }, [rooms, searchKeyword, sortConfig]);

  const totalPages = Math.ceil(filteredAndSortedRooms.length / pageSize);
  const paginatedRooms = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredAndSortedRooms.slice(start, start + pageSize);
  }, [filteredAndSortedRooms, currentPage, pageSize]);

  const fetchMembers = async (roomId: string) => {
    setLoadingMembers(true);
    try {
      const data = await api.get<{ items: RoomMember[] }>(`/api/talkrooms/${roomId}/members`);
      setMembers(Array.isArray(data?.items) ? data.items : []);
    } catch (error) {
      console.error('Failed to fetch members:', error);
      setMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  };

  const fetchAvailableUsers = async () => {
    try {
      const data = await api.get<{ items: User[] }>('/api/users?status=active');
      const users = Array.isArray(data?.items) ? data.items : [];
      setAvailableUsers(users);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  // ページ変更時にルーム一覧を先頭にスクロール
  useEffect(() => {
    roomListRef.current?.scrollTo({ top: 0 });
  }, [currentPage]);

  const handleCreateRoom = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newRoomName.trim() || creating) return;

    setCreating(true);
    try {
      const created = await api.post<TalkRoom>('/api/talkrooms', {
        name: newRoomName,
        description: newRoomDescription,
        roomType: newRoomType,
        memberIds: selectedMemberIds,
      });
      setRooms(prev => [created, ...prev]);
      setSelectedRoom(created);
      setSearchParams(prev => { prev.set('roomId', created.id); return prev; });
      setNewRoomName('');
      setNewRoomDescription('');
      setNewRoomType('public');
      setSelectedMemberIds([]);
      setShowCreateModal(false);
      // スマホではチャット画面に切り替え
      if (window.innerWidth < 1024) {
        setShowRoomList(false);
      }
    } catch (error) {
      console.error('Failed to create room:', error);
      setToast({ message: 'ルームの作成に失敗しました', type: 'error' });
    } finally {
      setCreating(false);
    }
  };

  // メンバー選択のトグル
  const toggleMemberSelection = (userId: string) => {
    setSelectedMemberIds(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleManageMembers = async () => {
    if (!selectedRoom) return;
    await fetchMembers(selectedRoom.id);
    setShowMemberModal(true);
  };

  const handleAddMember = async () => {
    if (!selectedRoom || !selectedUserId) return;

    try {
      await api.post(`/api/talkrooms/${selectedRoom.id}/members`, { userId: selectedUserId });
      setSelectedUserId('');
      await fetchMembers(selectedRoom.id);
      await fetchRooms(true);
    } catch (error) {
      console.error('Failed to add member:', error);
      setToast({ message: 'メンバーの追加に失敗しました', type: 'error' });
    }
  };

  const handleRemoveMember = async (member: RoomMember) => {
    if (!selectedRoom) return;
    setRemoveMemberTarget(member);
  };

  const confirmRemoveMember = async () => {
    if (!selectedRoom || !removeMemberTarget) return;
    try {
      await api.delete(`/api/talkrooms/${selectedRoom.id}/members/${removeMemberTarget.userId}`);
      await fetchMembers(selectedRoom.id);
      await fetchRooms(true);
    } catch (error) {
      console.error('Failed to remove member:', error);
      setToast({ message: 'メンバーの削除に失敗しました', type: 'error' });
    } finally {
      setRemoveMemberTarget(null);
    }
  };

  const handleLeaveRoom = async () => {
    if (!selectedRoom || leaving) return;
    setLeaving(true);
    try {
      await api.post(`/api/talkrooms/${selectedRoom.id}/leave`, {});
      setShowLeaveConfirm(false);
      setSelectedRoom(null);
      setSearchParams(prev => { prev.delete('roomId'); return prev; });
      await fetchRooms();
      setToast({ message: 'ルームから退出しました', type: 'success' });
    } catch (error) {
      console.error('Failed to leave room:', error);
      setToast({ message: 'ルームからの退出に失敗しました', type: 'error' });
    } finally {
      setLeaving(false);
    }
  };

  const handleBackToList = () => {
    setShowRoomList(true);
    // ルーム一覧の先頭にスクロール
    setTimeout(() => {
      roomListRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const getRoomTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      public: '公開',
      private: 'プライベート',
      direct: 'DM',
    };
    return labels[type] || type;
  };

  // 右パネルタブ切り替え（パネルが閉じていたら開く）
  const handleTabClick = (tab: RightPanelTabType) => {
    if (activeTab === tab && showRightPanel) {
      // 同じタブをクリックしたらパネルを閉じる
      setShowRightPanel(false);
    } else {
      setActiveTab(tab);
      setShowRightPanel(true);
    }
  };

  const nonMembers = availableUsers.filter(u => !members.some(m => m.userId === u.id));

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-8 h-8 border-4 border-green-200 border-t-green-600 rounded-full animate-spin" />
      </div>
    );
  }

  const rightPanelTabs = [
    { key: 'notes' as const, label: 'ノート', icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    )},
    { key: 'schedule' as const, label: '予定', icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    )},
    { key: 'folders' as const, label: 'フォルダ', icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
      </svg>
    )},
    { key: 'contents' as const, label: 'コンテンツ', icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    )},
  ];

  return (
    <div className="flex h-[calc(100dvh-56px)] lg:h-[calc(100dvh-140px)] bg-white dark:bg-gray-900 lg:rounded-xl lg:shadow-sm overflow-hidden -mx-4 -my-4 lg:mx-0 lg:my-0">
      {/* ========================================
          左カラム: ルーム一覧 - LINE風サイドバー
          ======================================== */}
      <div className={`${showRoomList ? 'flex' : 'hidden'} md:flex w-full md:w-72 lg:w-80 flex-col border-r border-gray-200 dark:border-gray-700 flex-shrink-0`}>
        {/* ヘッダー */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-800">
          <h1 className="text-lg font-bold text-gray-900 dark:text-white">トーク</h1>
          {canCreate('talkrooms') && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-blue-500 hover:bg-blue-600 text-white transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
            </button>
          )}
        </div>
        
        {/* 検索 */}
        <div className="p-3 border-b border-gray-100 dark:border-gray-800">
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              placeholder="トークルームを検索"
              className="w-full pl-9 pr-3 py-2 bg-gray-100 dark:bg-gray-800 border-0 rounded-full text-sm text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-green-500 focus:outline-none"
            />
          </div>
        </div>
        
        {/* フィルター */}
        <div className="px-3 py-2 flex gap-2 border-b border-gray-100 dark:border-gray-800">
          <Select
            value={`${sortConfig.key}-${sortConfig.direction}`}
            onChange={(e) => {
              const [key, direction] = e.target.value.split('-');
              const next = { key, direction: direction as 'asc' | 'desc' };
              setSortConfig(next);
              setSearchParams(prev => { prev.set('sortBy', next.key); prev.set('sortOrder', next.direction); prev.delete('page'); return prev; }, { replace: true });
            }}
            className="flex-1 text-xs bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 rounded-lg"
          >
            <option value="updatedAt-desc">最新順</option>
            <option value="updatedAt-asc">古い順</option>
            <option value="name-asc">名前（A-Z）</option>
            <option value="memberCount-desc">メンバー数</option>
          </Select>
          <Select
            value={pageSize.toString()}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="w-20 text-xs bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 rounded-lg"
          >
            <option value="10">10件</option>
            <option value="20">20件</option>
            <option value="50">50件</option>
          </Select>
        </div>
        
        {/* ルーム一覧 */}
        <div ref={roomListRef} className="flex-1 overflow-y-auto">
          {paginatedRooms.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400 p-4">
              <svg className="w-16 h-16 mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <p className="text-sm">{searchKeyword ? '該当するルームがありません' : 'トークルームがありません'}</p>
            </div>
          ) : (
            paginatedRooms.map(room => (
              <button
                key={room.id}
                onClick={() => {
                  setSelectedRoom(room);
                  setSearchParams(prev => { prev.set('roomId', room.id); return prev; });
                  if (window.innerWidth < 1024) {
                    setShowRoomList(false);
                  }
                }}
                className={`w-full px-4 py-3 flex items-center gap-3 text-left transition-colors border-b border-gray-50 dark:border-gray-800 ${
                  selectedRoom?.id === room.id
                    ? 'bg-blue-50 dark:bg-blue-900/20'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
                }`}
              >
                {/* アバター */}
                <div className={`relative w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0 shadow-sm ${
                  room.roomType === 'private' 
                    ? 'bg-gradient-to-br from-purple-400 to-purple-600' 
                    : room.roomType === 'direct' 
                      ? 'bg-gradient-to-br from-green-400 to-green-600' 
                      : 'bg-gradient-to-br from-blue-400 to-blue-600'
                }`}>
                  {room.roomType === 'private' ? (
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  ) : (
                    room.name.charAt(0).toUpperCase()
                  )}
                </div>
                
                {/* ルーム情報 */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <p className={`font-medium truncate ${
                        selectedRoom?.id === room.id 
                          ? 'text-blue-700 dark:text-blue-400' 
                          : 'text-gray-900 dark:text-white'
                      }`}>
                        {room.name}
                      </p>
                      {room.roomType === 'private' && (
                        <svg className="w-3.5 h-3.5 text-purple-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    <span className="text-[11px] text-gray-400 flex-shrink-0">
                      {formatRelativeTime(room.updatedAt)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-0.5">
                    <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                      {room.lastMessage || room.description || 'メッセージなし'}
                    </p>
                    <span className="text-[11px] text-gray-400 flex-shrink-0 bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">
                      {room.memberCount}人
                    </span>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
        
        {/* ページネーション */}
        {totalPages > 1 && (
          <div className="p-3 border-t border-gray-100 dark:border-gray-800 flex justify-center">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        )}
      </div>

      {/* ========================================
          中央カラム: チャットエリア（常時表示）
          ======================================== */}
      <div className={`${!showRoomList ? 'flex' : 'hidden'} md:flex flex-1 flex-col bg-gray-100 dark:bg-gray-900 overflow-hidden min-w-0`}>
        {selectedRoom ? (
          <>
            {/* チャットヘッダー */}
            <div className="flex items-center justify-between px-3 py-2 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
              <div className="flex items-center gap-2 min-w-0">
                <button
                  onClick={handleBackToList}
                  className="md:hidden p-1 -ml-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-sm flex-shrink-0 ${
                  selectedRoom.roomType === 'private' 
                    ? 'bg-gradient-to-br from-purple-400 to-purple-600' 
                    : 'bg-gradient-to-br from-blue-400 to-blue-600'
                }`}>
                  {selectedRoom.name.charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <h2 className="font-semibold text-sm text-gray-900 dark:text-white truncate">{selectedRoom.name}</h2>
                    <Badge variant="default" className="text-[10px] px-1 py-0">{getRoomTypeLabel(selectedRoom.roomType)}</Badge>
                  </div>
                  <p className="text-[11px] text-gray-500">{selectedRoom.memberCount}人</p>
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                {/* メンバー管理ボタン */}
                <button
                  onClick={handleManageMembers}
                  className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                  title="メンバー管理"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                  </svg>
                </button>
                {!isAdmin && (
                <button
                  onClick={() => setShowLeaveConfirm(true)}
                  className="p-2 text-gray-500 hover:text-red-500 dark:text-gray-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full transition-colors"
                  title="ルームから退出"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                </button>
                )}
                {/* 右パネル開閉ボタン */}
                <button
                  onClick={() => setShowRightPanel(!showRightPanel)}
                  className={`p-2 rounded-full transition-colors ${
                    showRightPanel
                      ? 'text-blue-600 bg-blue-50 hover:bg-blue-100 dark:text-blue-400 dark:bg-blue-900/30 dark:hover:bg-blue-900/50'
                      : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                  title={showRightPanel ? 'パネルを閉じる' : 'パネルを開く'}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
                  </svg>
                </button>
              </div>
            </div>

            {/* チャット本体（常時表示） */}
            <div className="flex-1 overflow-hidden">
              {currentUser?.id && (
                <ChatBox
                  entityType="talkroom"
                  entityId={selectedRoom.id}
                  currentUserId={currentUser.id}
                  title=""
                  className="h-full"
                />
              )}
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-400 bg-gradient-to-b from-blue-50 to-slate-100 dark:from-gray-800 dark:to-gray-900">
            <svg className="w-20 h-20 mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <p>トークルームを選択してください</p>
          </div>
        )}
      </div>

      {/* ========================================
          右カラム: タブエリア（ノート・予定・フォルダ・コンテンツ）
          ======================================== */}
      {selectedRoom && showRightPanel && (
        <div className="hidden md:flex w-80 lg:w-96 flex-col border-l border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex-shrink-0">
          {/* タブナビゲーション */}
          <div className="flex items-center border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            {rightPanelTabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => handleTabClick(tab.key)}
                className={`flex-1 flex items-center justify-center gap-1 px-2 py-3 text-xs font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === tab.key
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400 bg-blue-50/50 dark:bg-blue-900/10'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                }`}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            ))}
            {/* 閉じるボタン */}
            <button
              onClick={() => setShowRightPanel(false)}
              className="p-2 mx-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors flex-shrink-0"
              title="パネルを閉じる"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* タブコンテンツ */}
          <div className="flex-1 overflow-hidden">
            {activeTab === 'notes' && currentUser?.id && (
              <NotesTab roomId={selectedRoom.id} currentUserId={currentUser.id} />
            )}
            {activeTab === 'schedule' && (
              <ScheduleTab roomId={selectedRoom.id} />
            )}
            {activeTab === 'folders' && (
              <FoldersTab roomId={selectedRoom.id} />
            )}
            {activeTab === 'contents' && (
              <ContentsTab roomId={selectedRoom.id} />
            )}
          </div>
        </div>
      )}

      {/* ========================================
          モバイル用右パネル（オーバーレイ表示）
          ======================================== */}
      {selectedRoom && showRightPanel && (
        <>
          {/* オーバーレイ背景 */}
          <div
            className="md:hidden fixed inset-0 bg-black/50 z-40"
            onClick={() => setShowRightPanel(false)}
          />
          {/* スライドインパネル */}
          <div className="md:hidden fixed right-0 top-0 bottom-0 w-[85vw] max-w-sm bg-white dark:bg-gray-800 z-50 flex flex-col shadow-2xl animate-slide-in-right">
            {/* ヘッダー */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <h3 className="font-semibold text-gray-900 dark:text-white text-sm truncate">{selectedRoom.name}</h3>
              <button
                onClick={() => setShowRightPanel(false)}
                className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            {/* タブナビゲーション */}
            <div className="flex border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
              {rightPanelTabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex-1 flex items-center justify-center gap-1 px-2 py-2.5 text-xs font-medium whitespace-nowrap border-b-2 transition-colors ${
                    activeTab === tab.key
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  {tab.icon}
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
            {/* タブコンテンツ */}
            <div className="flex-1 overflow-hidden">
              {activeTab === 'notes' && currentUser?.id && (
                <NotesTab roomId={selectedRoom.id} currentUserId={currentUser.id} />
              )}
              {activeTab === 'schedule' && (
                <ScheduleTab roomId={selectedRoom.id} />
              )}
              {activeTab === 'folders' && (
                <FoldersTab roomId={selectedRoom.id} />
              )}
              {activeTab === 'contents' && (
                <ContentsTab roomId={selectedRoom.id} />
              )}
            </div>
          </div>
        </>
      )}

      {/* ルーム作成モーダル */}
      <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} title="新しいトークルーム">
        <form onSubmit={handleCreateRoom} className="space-y-4">
          <Input
            label="ルーム名"
            value={newRoomName}
            onChange={(e) => setNewRoomName(e.target.value)}
            required
            maxLength={100}
            placeholder="ルーム名を入力"
          />
          <Input
            label="説明"
            value={newRoomDescription}
            onChange={(e) => setNewRoomDescription(e.target.value)}
            placeholder="ルームの説明（任意）"
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">ルーム種別</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  value="public"
                  checked={newRoomType === 'public'}
                  onChange={() => setNewRoomType('public')}
                  className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">公開</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  value="private"
                  checked={newRoomType === 'private'}
                  onChange={() => setNewRoomType('private')}
                  className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">プライベート</span>
              </label>
            </div>
          </div>
          
          {/* 参加メンバー選択 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              参加メンバー
              {selectedMemberIds.length > 0 && (
                <span className="ml-2 text-blue-600">({selectedMemberIds.length}人選択中)</span>
              )}
            </label>
            <div className="border border-gray-300 dark:border-gray-600 rounded-lg max-h-48 overflow-y-auto">
              {availableUsers
                .filter(user => user.id !== currentUser?.id)
                .map(user => (
                  <label
                    key={user.id}
                    className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                      selectedMemberIds.includes(user.id) ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedMemberIds.includes(user.id)}
                      onChange={() => toggleMemberSelection(user.id)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-medium flex-shrink-0">
                      {user.name.charAt(0)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{user.name}</p>
                      <p className="text-xs text-gray-500 truncate">{user.email}</p>
                    </div>
                  </label>
                ))}
              {availableUsers.filter(u => u.id !== currentUser?.id).length === 0 && (
                <p className="px-3 py-4 text-sm text-gray-500 text-center">追加できるユーザーがいません</p>
              )}
            </div>
            <p className="mt-1 text-xs text-gray-500">※ あなたは自動的に追加されます</p>
          </div>
          
          <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-4">
            <Button type="button" variant="secondary" onClick={() => setShowCreateModal(false)} className="w-full sm:w-auto">
              キャンセル
            </Button>
            <Button 
              type="submit" 
              disabled={creating || !newRoomName.trim()} 
              className="w-full sm:w-auto"
            >
              {creating ? '作成中...' : '作成'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* メンバー管理モーダル */}
      <Modal
        isOpen={showMemberModal}
        onClose={() => setShowMemberModal(false)}
        title={`${selectedRoom?.name || ''} - メンバー`}
        size="lg"
      >
        <div className="space-y-6">
          {/* メンバー追加 */}
          {selectedRoom?.roomType !== 'public' && (
            <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">メンバーを追加</h3>
              <div className="flex gap-2">
                <Select
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                  className="flex-1"
                >
                  <option value="">ユーザーを選択...</option>
                  {nonMembers.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name} ({user.email})
                    </option>
                  ))}
                </Select>
                <Button 
                  onClick={handleAddMember} 
                  disabled={!selectedUserId}
                >
                  追加
                </Button>
              </div>
            </div>
          )}

          {/* メンバー一覧 */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              メンバー ({members.length}名)
            </h3>
            {loadingMembers ? (
              <div className="flex justify-center py-8">
                <div className="w-6 h-6 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
              </div>
            ) : members.length === 0 ? (
              <p className="text-center text-gray-500 py-8">メンバーがいません</p>
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {members.map((member) => (
                  <div
                    key={member.id}
                    className="flex items-center justify-between p-3 bg-white dark:bg-gray-900 border dark:border-gray-700 rounded-xl"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-sm">
                        {member.userName.charAt(0)}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white text-sm">{member.userName}</p>
                        <p className="text-xs text-gray-500">{member.userEmail}</p>
                      </div>
                    </div>
                    {member.userId !== currentUser?.id && selectedRoom?.roomType !== 'public' && (
                      <button
                        onClick={() => handleRemoveMember(member)}
                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end pt-4 border-t dark:border-gray-700">
            <Button variant="secondary" onClick={() => setShowMemberModal(false)}>
              閉じる
            </Button>
          </div>
        </div>
      </Modal>

      {/* メンバー削除確認モーダル */}
      <Modal isOpen={!!removeMemberTarget} onClose={() => setRemoveMemberTarget(null)} title="メンバーの削除" size="sm">
        <div className="space-y-4">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            {removeMemberTarget?.userName} をルームから削除しますか？
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setRemoveMemberTarget(null)}>キャンセル</Button>
            <Button onClick={confirmRemoveMember} className="bg-red-600 hover:bg-red-700">削除</Button>
          </div>
        </div>
      </Modal>

      {/* ルーム退出確認モーダル */}
      <Modal isOpen={showLeaveConfirm} onClose={() => setShowLeaveConfirm(false)} title="ルームから退出" size="sm">
        <div className="space-y-4">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            「{selectedRoom?.name}」から退出しますか？退出後はこのルームのメッセージを閲覧できなくなります。
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setShowLeaveConfirm(false)}>キャンセル</Button>
            <Button onClick={handleLeaveRoom} disabled={leaving} className="bg-red-600 hover:bg-red-700">
              {leaving ? '退出中...' : '退出'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-white text-sm ${
          toast.type === 'error' ? 'bg-red-600' : 'bg-green-600'
        }`}>
          {toast.message}
          <button onClick={() => setToast(null)} className="ml-3 font-bold">&times;</button>
        </div>
      )}
    </div>
  );
}
